import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconEraser,
    IconPhoto,
    IconTextSize,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { isHeicFile, toDisplayableBlob } from '../../image/shared/heicDisplay';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { applyImageWatermark, applyTextWatermark } from './watermarkPdfEngine';
import { detectWatermarks, removeWatermarks } from './watermarkRemoveEngine';
import WatermarkRemovePanel from './WatermarkRemovePanel';
import WatermarkInteractivePreview from './WatermarkInteractivePreview';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

const DEFAULT_TEXT = 'SohozKaj';
const DEFAULT_TEXT_COLOR = '#000000';
const DEFAULT_FONT_SIZE = 37;
const DEFAULT_TEXT_OPACITY = 17;
const DEFAULT_IMAGE_OPACITY = 30;
const DEFAULT_IMAGE_SCALE = 30;
const DEFAULT_X_PERCENT = 100;
const DEFAULT_Y_PERCENT = 89;
const DEFAULT_ROTATION = 0;

const inputClass = 'w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

function toPdfYPercent(uiYPercent) {
    return 100 - uiYPercent;
}

export default function WatermarkPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [mode, setMode] = useState('text');
    const [text, setText] = useState(DEFAULT_TEXT);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageNaturalSize, setImageNaturalSize] = useState(null);
    const [xPercent, setXPercent] = useState(DEFAULT_X_PERCENT);
    const [yPercent, setYPercent] = useState(DEFAULT_Y_PERCENT);
    const [rotationDegrees, setRotationDegrees] = useState(DEFAULT_ROTATION);
    const [textOpacity, setTextOpacity] = useState(DEFAULT_TEXT_OPACITY);
    const [imageOpacity, setImageOpacity] = useState(DEFAULT_IMAGE_OPACITY);
    const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
    const [imageScale, setImageScale] = useState(DEFAULT_IMAGE_SCALE);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    // ── Add or remove ────────────────────────────────────────────────────────
    // One tool, two jobs: a PDF that already carries a watermark almost always
    // means the person wants it gone, and one that does not means they want one
    // on. So the file is scanned as soon as it lands and the tool opens on the
    // job that fits it — the other is always one press away.
    const [task, setTask] = useState('add');
    const [scanning, setScanning] = useState(false);
    const [detection, setDetection] = useState(null);
    const [selectedMarks, setSelectedMarks] = useState([]);

    const clearApplyResult = () => {
        setResult(null);
        setError(null);
    };

    const loadPdf = useCallback((incoming) => {
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('watermarkPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('watermarkPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        setFile(incoming);
        setResult(null);
        setError(null);
        setDetection(null);
        setSelectedMarks([]);
        setTask('add');

        setScanning(true);
        detectWatermarks(incoming)
            .then((found) => {
                setDetection(found);
                setSelectedMarks(found.candidates.map((c) => c.id));
                // Everything found is ticked, and the tool opens on removal —
                // the person who uploads a stamped PDF is rarely here to add one.
                if (found.candidates.length) setTask('remove');
            })
            .catch(() => setDetection({ pageCount: 0, parsedPages: 0, candidates: [] }))
            .finally(() => setScanning(false));
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadPdf(files[0]));

    const loadImage = useCallback(async (incoming) => {
        if (!incoming) return;
        // Chrome on Windows reports `type === ''` for a .heic, so ask by name too.
        if (!(incoming.type || '').startsWith('image/') && !isHeicFile(incoming)) {
            const msg = t('watermarkPdf.errors.invalidImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_IMAGE_BYTES) {
            const msg = t('watermarkPdf.errors.imageTooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        // HEIC is decoded first — a raw .heic preview paints nothing outside Safari.
        const drawable = await toDisplayableBlob(incoming);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        const url = URL.createObjectURL(drawable);
        setImageFile(incoming);
        setImagePreview(url);
        setImageNaturalSize(null);
        setResult(null);
        setError(null);

        const img = new Image();
        img.onload = () => {
            setImageNaturalSize({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 });
        };
        img.src = url;
    }, [imagePreview, t]);

    const handlePdfDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadPdf(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setDetection(null);
        setSelectedMarks([]);
        setTask('add');
    };

    const clearImage = () => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(null);
        setImagePreview(null);
        setImageNaturalSize(null);
        setResult(null);
        setError(null);
    };

    const handlePositionChange = useCallback(({ xPercent: nextX, yPercent: nextY }) => {
        setXPercent(nextX);
        setYPercent(nextY);
        clearApplyResult();
    }, []);

    const handleFontSizeChange = useCallback((next) => {
        setFontSize(next);
        clearApplyResult();
    }, []);

    const handleImageScaleChange = useCallback((next) => {
        setImageScale(next);
        clearApplyResult();
    }, []);

    const handleRotationChange = useCallback((next) => {
        setRotationDegrees(next);
        clearApplyResult();
    }, []);

    const handleTextChange = useCallback((next) => {
        setText(next);
        setError(null);
        clearApplyResult();
    }, []);

    const handleRemove = async () => {
        if (!file || processing || !selectedMarks.length) return;
        recordToolUsage('pdf/watermark', { category: 'pdf' });
        setProcessing(true);
        setResult(null);
        setError(null);
        try {
            const { blob, removed } = await removeWatermarks(file, selectedMarks);
            const base = file.name.replace(/\.pdf$/i, '');
            setResult({ name: `${base}-no-watermark.pdf`, blob });
            notifications.show({
                title: t('common.success'),
                message: t('watermarkPdf.remove.done', { count: toBnDigits(removed) }),
                color: 'green',
            });
        } catch (err) {
            const msg = err?.message === 'nothing'
                ? t('watermarkPdf.remove.errors.nothing')
                : t('watermarkPdf.remove.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handleApply = async () => {
        if (!file || processing) return;
        if (mode === 'text' && !text.trim()) {
            const msg = t('watermarkPdf.errors.emptyText');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (mode === 'image' && !imageFile) {
            const msg = t('watermarkPdf.errors.noImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('pdf/watermark', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const blob = mode === 'text'
                ? await applyTextWatermark(file, {
                    text,
                    textColor,
                    opacity: textOpacity,
                    fontSize,
                    xPercent,
                    yPercent: toPdfYPercent(yPercent),
                    rotationDegrees,
                })
                : await applyImageWatermark(file, imageFile, {
                    opacity: imageOpacity,
                    xPercent,
                    yPercent: toPdfYPercent(yPercent),
                    rotationDegrees,
                    scale: imageScale,
                });

            const base = file.name.replace(/\.pdf$/i, '');
            setResult({ name: `${base}-watermarked.pdf`, blob });
            notifications.show({ title: t('common.success'), message: t('watermarkPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('watermarkPdf.errors.failed');
            if (err?.message === 'password') msg = t('watermarkPdf.errors.password');
            else if (err?.message === 'emptyText') msg = t('watermarkPdf.errors.emptyText');
            else if (err?.message === 'noImage') msg = t('watermarkPdf.errors.noImage');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const removing = task === 'remove';
    const canApply = file && !processing && !scanning && (removing
        ? selectedMarks.length > 0
        : (mode === 'text' ? text.trim() : imageFile));

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!canApply,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        ready: scanning ? t('watermarkPdf.remove.scanning') : undefined,
        success: removing ? t('watermarkPdf.remove.doneShort') : t('watermarkPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const fileMeta = file ? formatFileSize(file.size) : '';
    const successLabel = result
        ? `${t('watermarkPdf.done')} · ${formatFileSize(result.blob.size)}`
        : '';
    const handleReset = () => {
        clearFile();
        clearImage();
        setTask('add');
        setMode('text');
        setText(DEFAULT_TEXT);
        setTextColor(DEFAULT_TEXT_COLOR);
        setXPercent(DEFAULT_X_PERCENT);
        setYPercent(DEFAULT_Y_PERCENT);
        setRotationDegrees(DEFAULT_ROTATION);
        setTextOpacity(DEFAULT_TEXT_OPACITY);
        setImageOpacity(DEFAULT_IMAGE_OPACITY);
        setFontSize(DEFAULT_FONT_SIZE);
        setImageScale(DEFAULT_IMAGE_SCALE);
    };

    return (
        <ToolPageLayout
            slug="pdf/watermark"
            subtitleKey="watermarkPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: removing ? t('watermarkPdf.remove.apply') : t('watermarkPdf.apply'),
                onClick: removing ? handleRemove : handleApply,
                disabled: !canApply || !!result,
                loading: processing,
                icon: removing ? IconEraser : undefined,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadPdf(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/avif,image/heic,image/heif,.png,.jpg,.jpeg,.webp,.avif,.heic,.heif,.hif"
                        className="hidden"
                        onChange={(e) => { loadImage(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('watermarkPdf.dropTitle')}
                            hint={t('watermarkPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handlePdfDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearFile}
                            removeLabel={t('watermarkPdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <>
                    {/* Which job this file needs. The scan picks the opening tab;
                        this is how somebody overrides it — to add a second
                        watermark to a stamped file, or to go looking for one the
                        scan did not flag. */}
                    <ToolCard title={t('watermarkPdf.taskTitle')}>
                        <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-800">
                            {[
                                { id: 'add', label: t('watermarkPdf.taskAdd'), Icon: IconWand },
                                { id: 'remove', label: t('watermarkPdf.taskRemove'), Icon: IconEraser },
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => { setTask(item.id); setError(null); clearApplyResult(); }}
                                    aria-pressed={task === item.id}
                                    className={`inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[12.5px] font-semibold transition-colors ${
                                        task === item.id
                                            ? 'bg-white text-primary shadow-sm dark:bg-zinc-900'
                                            : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
                                    }`}
                                >
                                    <item.Icon size={15} />
                                    {item.label}
                                    {item.id === 'remove' && detection?.candidates?.length ? (
                                        <span className="ml-0.5 rounded-full bg-primary/15 px-1.5 text-[10.5px] text-primary">
                                            {toBnDigits(detection.candidates.length)}
                                        </span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                        {scanning && (
                            <p className="mt-2 text-[11.5px] text-zinc-400 dark:text-zinc-500">
                                {t('watermarkPdf.remove.scanning')}
                            </p>
                        )}
                    </ToolCard>

                    {removing ? (
                        <WatermarkRemovePanel
                            scanning={scanning}
                            detection={detection}
                            selected={selectedMarks}
                            onToggle={(id) => {
                                setSelectedMarks((prev) => (prev.includes(id)
                                    ? prev.filter((x) => x !== id)
                                    : [...prev, id]));
                                clearApplyResult();
                            }}
                        />
                    ) : (
                    <>
                    <ToolCard title={t('watermarkPdf.typeTitle')}>
                        <div className="space-y-3">
                            {/* Icons only — the type's name is in the tooltip and read
                                out via aria-label. */}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setMode('text'); setError(null); clearApplyResult(); }}
                                    title={t('watermarkPdf.modeText')}
                                    aria-label={t('watermarkPdf.modeText')}
                                    aria-pressed={mode === 'text'}
                                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        mode === 'text'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    <IconTextSize size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setMode('image'); setError(null); clearApplyResult(); }}
                                    title={t('watermarkPdf.modeImage')}
                                    aria-label={t('watermarkPdf.modeImage')}
                                    aria-pressed={mode === 'image'}
                                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        mode === 'image'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    <IconPhoto size={18} />
                                </button>
                            </div>

                            {mode === 'text' ? (
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <label htmlFor="wm-text" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.textLabel')}
                                        </label>
                                        <input
                                            id="wm-text"
                                            type="text"
                                            value={text}
                                            onChange={(e) => { setText(e.target.value); setError(null); clearApplyResult(); }}
                                            placeholder={t('watermarkPdf.textPlaceholder')}
                                            className={inputClass}
                                        />
                                        <p className="text-xs text-zinc-400">{t('watermarkPdf.textHint')}</p>
                                    </div>

                                    <div className="space-y-1.5">
                                        <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.textColorLabel')}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={textColor}
                                                onChange={(e) => { setTextColor(e.target.value); setError(null); clearApplyResult(); }}
                                                className="h-10 w-12 cursor-pointer rounded-lg border border-zinc-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900"
                                            />
                                            <input
                                                type="text"
                                                value={textColor}
                                                onChange={(e) => { setTextColor(e.target.value); setError(null); clearApplyResult(); }}
                                                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 font-mono text-[12px] text-zinc-900 outline-none transition-colors focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="wm-font-size" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.fontSizeLabel', { value: toBnDigits(fontSize) })}
                                        </label>
                                        <input
                                            id="wm-font-size"
                                            type="range"
                                            min={10}
                                            max={200}
                                            value={fontSize}
                                            onChange={(e) => { setFontSize(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                            className="w-full accent-primary"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="wm-text-opacity" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.opacityLabel', { value: toBnDigits(textOpacity) })}
                                        </label>
                                        <input
                                            id="wm-text-opacity"
                                            type="range"
                                            min={0}
                                            max={100}
                                            value={textOpacity}
                                            onChange={(e) => { setTextOpacity(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                            className="w-full accent-primary"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {!imageFile ? (
                                        <button
                                            type="button"
                                            onClick={() => imageInputRef.current?.click()}
                                            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-200 bg-zinc-50/80 px-4 py-8 transition-colors hover:border-primary/60 dark:border-zinc-700 dark:bg-zinc-800/50"
                                        >
                                            <IconPhoto size={24} className="text-primary" />
                                            <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{t('watermarkPdf.imageUpload')}</span>
                                            <span className="text-xs text-zinc-400">{t('watermarkPdf.imageHint')}</span>
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-3 rounded-xl border border-zinc-200 p-3 dark:border-zinc-700">
                                            {imagePreview && (
                                                <img src={imagePreview} alt="" className="h-14 w-14 rounded-lg border border-zinc-200 object-contain dark:border-zinc-700" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-zinc-800 dark:text-zinc-100">{imageFile.name}</p>
                                                <p className="text-xs text-zinc-400">{formatFileSize(imageFile.size)}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={clearImage}
                                                className="rounded-lg border border-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-600 transition-colors hover:border-red-200 hover:text-red-500 dark:border-zinc-700 dark:text-zinc-300"
                                                aria-label={t('watermarkPdf.removeImage')}
                                            >
                                                {t('watermarkPdf.removeImage')}
                                            </button>
                                        </div>
                                    )}
                                    <div className="space-y-1.5">
                                        <label htmlFor="wm-scale" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.scaleLabel', { value: toBnDigits(imageScale) })}
                                        </label>
                                        <input
                                            id="wm-scale"
                                            type="range"
                                            min={5}
                                            max={100}
                                            value={imageScale}
                                            onChange={(e) => { setImageScale(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                            className="w-full accent-primary"
                                        />
                                        <p className="text-xs text-zinc-400">{t('watermarkPdf.scaleHint')}</p>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="wm-image-opacity" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                            {t('watermarkPdf.imageOpacityLabel', { value: toBnDigits(imageOpacity) })}
                                        </label>
                                        <input
                                            id="wm-image-opacity"
                                            type="range"
                                            min={0}
                                            max={100}
                                            value={imageOpacity}
                                            onChange={(e) => { setImageOpacity(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                            className="w-full accent-primary"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </ToolCard>

                    <ToolCard title={t('watermarkPdf.positionTitle')}>
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <label htmlFor="wm-x" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('watermarkPdf.horizontalLabel', { value: toBnDigits(xPercent) })}
                                </label>
                                <input
                                    id="wm-x"
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={xPercent}
                                    onChange={(e) => { setXPercent(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                    className="w-full accent-primary"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="wm-y" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('watermarkPdf.verticalLabel', { value: toBnDigits(yPercent) })}
                                </label>
                                <input
                                    id="wm-y"
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={yPercent}
                                    onChange={(e) => { setYPercent(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                    className="w-full accent-primary"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label htmlFor="wm-rotation" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                    {t('watermarkPdf.rotationLabel', { value: toBnDigits(rotationDegrees) })}
                                </label>
                                <input
                                    id="wm-rotation"
                                    type="range"
                                    min={-180}
                                    max={180}
                                    value={rotationDegrees}
                                    onChange={(e) => { setRotationDegrees(Number(e.target.value)); setError(null); clearApplyResult(); }}
                                    className="w-full accent-primary"
                                />
                            </div>
                        </div>
                    </ToolCard>
                    </>
                    )}
                </>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={processing ? t('watermarkPdf.applying') : undefined}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('watermarkPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? clearApplyResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('watermarkPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('watermarkPdf.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handlePdfDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && !error}
                >
                    {file && !error ? (
                        result ? (
                            <PdfDocPreview source={result.blob} />
                        ) : removing ? (
                            /* Nothing to place while removing: the draggable box
                               is for the watermark being ADDED, and leaving it
                               on screen in the other mode reads as though the
                               tool is about to stamp the file it is cleaning. */
                            <PdfDocPreview source={file} />
                        ) : (
                            <WatermarkInteractivePreview
                                file={file}
                                mode={mode}
                                text={text}
                                textColor={textColor}
                                fontSize={fontSize}
                                textOpacity={textOpacity}
                                imagePreview={imagePreview}
                                imageNaturalSize={imageNaturalSize}
                                imageScale={imageScale}
                                imageOpacity={imageOpacity}
                                xPercent={xPercent}
                                yPercent={yPercent}
                                rotationDegrees={rotationDegrees}
                                onPositionChange={handlePositionChange}
                                onFontSizeChange={handleFontSizeChange}
                                onImageScaleChange={handleImageScaleChange}
                                onRotationChange={handleRotationChange}
                                onTextChange={handleTextChange}
                            />
                        )
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
